import { AuditInfoDto } from "../../audit/AuditInfoDto";
import { CadastralNumbersDto } from "../../cadastral/CadastralNumbers";
import { ChimneyFieldsDto } from "../ChimneyFields";
import { FireplaceFieldsDto } from "../fireplace/FireplaceFields";
import { FlueFields, FlueFieldsDto } from "./FlueFields";
import { FlueId } from "./FlueId";
import { GeolocationDto } from "../../geo/Geolocation";
import { MunicipalityDto } from "../../municipality/Municipality";
import { PostalAddressDto } from "../../address/PostalAddress";
import {
  StreetAddressContainerDto,
  StreetAddressDto,
} from "../../address/StreetAddress";
import { DateTimeISO } from "shared-ts-types";
import { DateTime } from "luxon";
import { DateTimeUtils } from "shared-ts-utils-luxon";
import { notUndef } from "shared-ts-utils-core";
import { TFunction } from "i18next";

export interface FlueDto extends StreetAddressContainerDto {
  id: FlueId;
  sensorOperationalAt?: DateTimeISO;
  comment: string;
  tags: string;
  geoLocation: GeolocationDto;
  municipality: MunicipalityDto;
  postalAddress: PostalAddressDto;
  streetAddress: StreetAddressDto;
  cadastralNumbers: CadastralNumbersDto;
  chimney: ChimneyFieldsDto;
  flue: FlueFieldsDto;
  fireplace: FireplaceFieldsDto;
  audit: AuditInfoDto;
}

export class Flue {
  readonly id: FlueId;
  readonly sensorOperationalAt?: DateTime<true>;
  readonly comment: string;
  readonly tags: string;
  readonly geoLocation: GeolocationDto;
  readonly municipality: MunicipalityDto;
  readonly postalAddress: PostalAddressDto;
  readonly streetAddress: StreetAddressDto;
  readonly cadastralNumbers: CadastralNumbersDto;
  readonly chimney: ChimneyFieldsDto;
  readonly flue: FlueFields;
  readonly fireplace: FireplaceFieldsDto;
  readonly audit: AuditInfoDto;
  private readonly attentions: Array<string>;
  constructor(readonly dto: FlueDto | Flue) {
    if (dto instanceof Flue) {
      this.id = dto.id;
      this.sensorOperationalAt = dto.sensorOperationalAt;
      this.comment = dto.comment;
      this.tags = dto.tags;
      this.geoLocation = dto.geoLocation;
      this.municipality = dto.municipality;
      this.postalAddress = dto.postalAddress;
      this.streetAddress = dto.streetAddress;
      this.cadastralNumbers = dto.cadastralNumbers;
      this.chimney = dto.chimney;
      this.flue = dto.flue;
      this.fireplace = dto.fireplace;
      this.audit = dto.audit;
      this.attentions = [];
    } else {
      this.id = dto.id;
      this.sensorOperationalAt = notUndef(dto.sensorOperationalAt, (it) =>
        DateTimeUtils.fromISO(it)
      );
      this.comment = dto.comment;
      this.tags = dto.tags;
      this.geoLocation = dto.geoLocation;
      this.municipality = dto.municipality;
      this.postalAddress = dto.postalAddress;
      this.streetAddress = dto.streetAddress;
      this.cadastralNumbers = dto.cadastralNumbers;
      this.chimney = dto.chimney;
      this.flue = new FlueFields(dto.flue);
      this.fireplace = dto.fireplace;
      this.audit = dto.audit;
      this.attentions = [];
    }
  }

  getDisplayName(): string {
    let str = this.streetAddress.street;
    if (this.streetAddress.houseNumber != null) {
      str += ` ${this.streetAddress.houseNumber}`;
    }
    if (this.streetAddress.houseLetter != null) {
      str += this.streetAddress.houseLetter.toLowerCase();
    }
    if (this.flue != null && this.flue.name?.isNotBlank()) {
      str += " / " + this.flue.name;
    }
    return str;
  }

  getAttentions(t: TFunction<"firefly-shared-ts-domain">): Array<string> {
    return this.attentions;
  }

  toDto(): FlueDto {
    return {
      id: this.id,
      sensorOperationalAt: this.sensorOperationalAt?.toISO(),
      comment: this.comment,
      tags: this.tags,
      geoLocation: this.geoLocation,
      municipality: this.municipality,
      postalAddress: this.postalAddress,
      streetAddress: this.streetAddress,
      cadastralNumbers: this.cadastralNumbers,
      chimney: this.chimney,
      flue: this.flue.toDto(),
      fireplace: this.fireplace,
      audit: this.audit,
    };
  }
}
